'use client';
import { useState } from 'react';
import './evaluation-panel.css';
import { currentDomainHref } from '../../lib/domain-links.js';
import productProfiles from './evaluation-profiles.json' with { type: 'json' };

type Product = 'Discover' | 'Provenance' | 'Response';
const profiles = productProfiles;
export function EvaluationPanel({ product }: { product?: Product }) {
  const [selected, setSelected] = useState<Product>(product || 'Discover');
  const [baseline, setBaseline] = useState('Existing manual investigation');
  const [status, setStatus] = useState('');
  const profile = profiles[selected];
  const brief = [
    selected + ' evaluation brief',
    'Question: ' + profile.question,
    'Starting input: ' + profile.input,
    'Expected review artifact: ' + profile.output,
    'Compare against: ' + baseline,
    'Measure: ' + profile.measure,
    'Safety boundary: ' + profile.boundary,
    '',
    'Deployment: on-site, with local models and processing in the air-gapped configuration. IT/Cyber imports approved software, NVD and threat-intelligence updates. Optional outbound retrieval requires an explicitly connected window.',
    'Before access: confirm hardware, local model dependencies, offline update procedure, retention, owners, supported integrations and commercial terms.',
    'Acceptance: agree a baseline and target with the team before testing. Record gaps and reasons to stop, not just successful examples.',
    'Next step: share this brief with your evaluation sponsor. This download does not request an account or schedule a demonstration.',
    'Download the one-page PDF for the selected product and baseline. No investigation content or personal data is submitted.'
  ].join('\n\n');
  async function download() {
    try {
      const key = baseline.includes('scanner') ? 'scanner' : baseline.includes('approval') ? 'approval' : 'manual';
      const filename = selected.toLowerCase() + '-' + key + '-evaluation-brief.pdf';
      const response = await fetch('/evaluation-briefs/' + filename);
      if (!response.ok || !response.headers.get('content-type')?.includes('application/pdf')) throw new Error('Brief unavailable');
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url; link.download = filename;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setStatus('Your one-page PDF is ready. Check your downloads.');
    } catch {
      setStatus('The download could not start. Open the brief preview below and copy its text.');
    }
  }
  return <section className="evaluation-panel" id="evaluate" aria-labelledby="evaluation-title">
    <div className="evaluation-buyer-answers" id="buyer-trust">
      <p className="evaluation-eyebrow">Fit, deployment and a practical first step</p>
      {!product && <label htmlFor="evaluation-product">Your starting product</label>}
      {product ? <p className="evaluation-product-name">{product}</p> : <select id="evaluation-product" value={selected} onChange={e => { setSelected(e.target.value as Product); setStatus(''); }}>
        <option value="Discover">Discover · infrastructure exposure</option>
        <option value="Provenance">Provenance · application evidence</option>
        <option value="Response">Response · investigation and response</option>
      </select>}
      <h2>Is {selected} right for your team?</h2>
      <p>On-site security software. Local operation. Your team controls access and updates.</p>
      <div className="evaluation-trust-grid">{[
        ['Who is this for?', productProfiles[selected].audience],
        ['When should we consider it?', productProfiles[selected].consider],
        ['When is it not a fit?', productProfiles[selected].nonfit],
        ['How do we get started?', 'Choose one authorized case and a named owner. '+productProfiles[selected].input+' Install on-site, confirm local dependencies, start read-only and agree evaluation targets.'],
        ['What data leaves our environment?', 'In the air-gapped configuration, processing and operational data remain on-site. IT/Cyber imports approved software, NVD and other threat-intelligence packages. Optional outbound update retrieval needs explicitly permitted connectivity; that window is not air-gapped. Review the endpoints and any data sent before enabling it.'],
        ['What does a successful evaluation look like?', productProfiles[selected].output+' Measure '+productProfiles[selected].measure.toLowerCase()+' Agree targets first and record failures and gaps.'],
        ['What needs to be confirmed before purchase?', 'Hardware and local-model sizing, supported versions and adapters, offline update packaging, feed freshness, retention, identity integration, recovery, licensing and support. Production deployment is on-site, not SaaS.']
      ].map(([question, answer], i) => <details key={question} open={i === 0}><summary>{question}</summary><p>{answer}</p></details>)}</div>
      <a className="evaluation-trust-link" href="https://www.bwtr.ai/architecture">Explore architecture diagrams, trust boundaries and implementation notes ↗</a>
    </div>
    <div className="evaluation-intro">
      <p className="evaluation-eyebrow">Before you connect a system</p>
      <h2 id="evaluation-title">{product ? 'Evaluate ' + product + ' on a question that matters.' : 'Choose the question. Then choose the product.'}</h2>
      <p>A useful evaluation starts with one bounded case—not a promise that every workflow fits.</p>
      <ol className="evaluation-steps">
        <li><strong>Agree the starting point.</strong><span>Choose authorized inputs and compare with your existing process.</span></li>
        <li><strong>Inspect a concrete result.</strong><span>Check sources, missing context, and who owns the next decision.</span></li>
        <li><strong>Decide whether to expand.</strong><span>Measure effort and evidence quality. Keep a record of what did not work.</span></li>
      </ol>
      <a className="evaluation-trust-link" href="#buyer-trust">Review deployment and purchasing questions ↑</a>
    </div>
    <div className="evaluation-card">
      <p className="evaluation-product-name">{selected}</p>
      <h3>{profile.question}</h3>
      <dl><dt>Bring</dt><dd>{profile.input}</dd><dt>Inspect</dt><dd>{profile.output}</dd></dl>
      <label htmlFor="evaluation-baseline">Compare with your current approach</label>
      <select id="evaluation-baseline" value={baseline} onChange={e => { setBaseline(e.target.value); setStatus(''); }}>
        <option>Existing manual investigation</option><option>Existing scanner review process</option><option>Existing operational approval process</option>
      </select>
      <p className="evaluation-measure"><strong>Measure, do not assume.</strong> {profile.measure}</p>
      <button type="button" className="evaluation-download" onClick={download}>Download evaluation brief</button>
      {!product && <a className="evaluation-product-link" href={currentDomainHref(profile.href)}>Explore {selected} →</a>}
      <p className="evaluation-local">One-page PDF for your selected comparison. No account or form submission.</p>
      <p className="evaluation-status" role="status">{status}</p>
      <details className="evaluation-preview"><summary>Preview the brief</summary><pre>{brief}</pre></details>
    </div>
  </section>;
}
